// src/components/CuratedFikaCancellation.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';

const CuratedFikaCancellation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, confirming, success, error
  const [message, setMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(true);

  const sessionId = searchParams.get('session');
  const participantId = searchParams.get('participant');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!sessionId || !participantId || !token) {
      setStatus('error');
      setMessage('Invalid cancellation link. Please contact support.');
      setShowConfirmation(false);
    } else {
      setStatus('confirming');
    }
  }, [sessionId, participantId, token]);

  const handleCancellation = async () => {
    setStatus('loading');
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/curated-fika-admin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'cancel_participant_attendance',
            data: {
              sessionId,
              participantId,
              token,
              cancellationReason: 'User cancelled via email link'
            }
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        setMessage('Your attendance has been cancelled successfully.');
      } else {
        setStatus('error');
        setMessage(result.error || 'Failed to cancel attendance. Please contact support.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error occurred. Please try again or contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 py-32">
      <div className="max-w-md mx-auto px-6">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          
          {status === 'confirming' && (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Cancel Attendance?
              </h1>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to cancel your attendance for this Curated Fika session?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/events')}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Keep My Spot
                </button>
                <button
                  onClick={handleCancellation}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Cancel Attendance
                </button>
              </div>
            </>
          )}

          {status === 'loading' && (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Processing Cancellation...
              </h1>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Cancellation Confirmed
              </h1>
              <p className="text-gray-600 text-center mb-6">
                {message}
              </p>
              <button
                onClick={() => navigate('/events')}
                className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Browse Other Events
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Cancellation Failed
              </h1>
              <p className="text-red-600 text-center mb-6">
                {message}
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Contact Support
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CuratedFikaCancellation;
